import { Form, Input, Row, Col, DatePicker, InputNumber, Select, Card, Button, Typography } from 'antd';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import FormPage from '../../../../../../components/pages/FormPage';
import moment from 'moment';
import { PlusOutlined, DeleteOutlined, ThunderboltOutlined } from '@ant-design/icons';
import kdKantor from '../../../../../../assets/kode_kantor.json';
import ALASAN_LIST from '../../../../../../assets/list_alasan.json';
import kdKemas from '../../../../../../assets/kode_kemas.json';

const DATE_FMT = 'DD-MM-YYYY';
const DATE_PICK_FMT = 'DD-MMM-YYYY';

const fmtDate = (d) => {
    if (!d) return '';
    const m = moment(d);
    return m.isValid() ? m.format(DATE_FMT) : '';
};

const DefKemasan = () => ({
    jenisKemasan: '',
    jumlahKemasan: '',
    nomorBlAwb: '',
    tanggalBlAwb: '',
});

const DefKontainer = () => ({
    jenisMuat: '',
    nomorKontainer: '',
    ukuranKontainer: '',
});

const DefModel = {
    _id: null,
    // header
    nomorSurat: '',
    tipeData: '',
    tanggalSurat: '',
    kodeKantor: '050100',
    kodeTpsAsal: '',
    gudangAsal: '',
    kodeTpsTujuan: '',
    gudangTujuan: '',
    kodeAlasanPlp: '',
    alasanPenunjukan: '',
    yorAsal: '',
    yorTujuan: '',
    callSign: '',
    namaAngkut: '',
    noVoyFlight: '',
    tanggalTiba: '',
    nomorPos: '',
    nomorBc11: '',
    tanggalBc11: '',
    namaPemohon: '',
    // detil (satu grup detil berisi list kemasan + kontainer)
    kemasan: [DefKemasan()],
    kontainer: [DefKontainer()],
    isCreate: true
};

// Contoh payload API untuk tombol "Isi Otomatis"
const SAMPLE_DATA = {
    detil: [
        {
            kemasan: [
                {
                    jenisKemasan: 'PK',
                    jumlahKemasan: 5,
                    nomorBlAwb: '406-93764506',
                    tanggalBlAwb: '28-09-2026',
                },
            ],
            kontainer: [
                {
                    jenisMuat: '7',
                    nomorKontainer: '',
                    ukuranKontainer: '',
                },
            ],
        },
    ],
    header: {
        callSign: 'RAM',
        gudangAsal: 'UNEX',
        gudangTujuan: 'CENI',
        kodeAlasanPlp: '5',
        kodeKantor: '050100',
        kodeTpsAsal: 'UNX1',
        kodeTpsTujuan: 'CEN1',
        namaAngkut: 'Royal Air Maroc',
        namaPemohon: 'ABDULLAH AZZAM',
        noVoyFlight: 'AT850',
        nomorBc11: '085894',
        nomorSurat: '074474',
        refNumber: 'UNX1260923000120',
        tanggalBc11: '28-09-2026',
        tanggalSurat: '28-09-2026',
        tanggalTiba: '28-09-2026',
        tipeData: '0',
        tpsTujuan: {
            alasanPenunjukan: 'Barang impor konsolidasi dalam 1(satu) master airway bill atau master bill of lading',
            gudangTujuan: 'CENI',
            kodeTpsTujuan: 'CEN1',
            yorTujuan: 0,
        },
        yorAsal: 0,
        yorTujuan: 0,
    },
};

export default () => {
    const [model, setModel] = useState(DefModel);
    const { warehouseCache } = useSelector((state) => state.apps);
    const warehouseOpts = (warehouseCache || []).map((w) => ({
        value: w.kode_tps,
        label: `${w.kode_tps} - ${w.nama || ''}`.trim(),
    }));
    const gudangOpts = (warehouseCache || []).map((w) => ({
        value: w.kode_warehouse,
        label: `${w.kode_warehouse} - ${w.nama || ''}`.trim(),
    }));
    const kemasanOpts = (kdKemas || []).map((k) => ({
        value: (k.value || '').trim(),
        label: `${(k.value || '').trim()} - ${(k.name || '').trim()}`,
    }));

    const set = (patch) => setModel((prev) => ({ ...prev, ...patch }));

    const handleDateChange = (name, date) => {
        set({ [name]: date });
    };

    // ---- kemasan handlers ----
    const updateKemasan = (idx, patch) => {
        const list = [...(model.kemasan || [])];
        list[idx] = { ...list[idx], ...patch };
        set({ kemasan: list });
    };
    const addKemasan = () => set({ kemasan: [...(model.kemasan || []), DefKemasan()] });
    const removeKemasan = (idx) => {
        const list = (model.kemasan || []).filter((_, i) => i !== idx);
        set({ kemasan: list.length ? list : [DefKemasan()] });
    };

    // ---- kontainer handlers ----
    const updateKontainer = (idx, patch) => {
        const list = [...(model.kontainer || [])];
        list[idx] = { ...list[idx], ...patch };
        set({ kontainer: list });
    };
    const addKontainer = () => set({ kontainer: [...(model.kontainer || []), DefKontainer()] });
    const removeKontainer = (idx) => {
        const list = (model.kontainer || []).filter((_, i) => i !== idx);
        set({ kontainer: list.length ? list : [DefKontainer()] });
    };

    const handleAlasanChange = (v) => {
        const found = ALASAN_LIST.find((a) => a.value === String(v));
        set({
            kodeAlasanPlp: String(v),
            // auto-isi alasanPenunjukan jika masih kosong, user tetap bisa edit manual
            ...(found && !model.alasanPenunjukan ? { alasanPenunjukan: found.short } : {}),
        });
    };

    // mapping data edit -> bentuk form flat
    // API bisa mengembalikan bentuk nested lama (header/detil) atau flat baru
    // (tanggalSurat, kemasan, ... langsung di root). Keduanya dinormalisasi ke sini.
    const toMoment = (v) => {
        if (!v) return null;
        if (moment.isMoment(v)) return v.isValid() ? v : null;
        const m = moment(v, [DATE_FMT, DATE_PICK_FMT, 'YYYYMMDD', moment.ISO_8601], true);
        return m.isValid() ? m : null;
    };
    const mapEditToForm = (s) => {
        if (!s) return s;
        const h = s.header || s;
        const d0 = (s.detil || [])[0] || {};
        const kemasanSrc = (d0.kemasan || s.kemasan || []);
        const kontainerSrc = (d0.kontainer || s.kontainer || []);
        return {
            ...DefModel,
            _id: s._id || s.id || null,
            isCreate: false,
            nomorSurat: h.nomorSurat || s.nomorSurat || '',
            tipeData: String(h.tipeData ?? s.tipeData ?? '0'),
            tanggalSurat: toMoment(h.tanggalSurat ?? s.tanggalSurat),
            kodeKantor: h.kodeKantor || s.kodeKantor || '',
            kodeTpsAsal: h.kodeTpsAsal || s.kodeTpsAsal || '',
            gudangAsal: h.gudangAsal || s.gudangAsal || '',
            kodeTpsTujuan: h.kodeTpsTujuan || s.kodeTpsTujuan || '',
            gudangTujuan: h.gudangTujuan || s.gudangTujuan || '',
            kodeAlasanPlp: String(h.kodeAlasanPlp ?? s.kodeAlasanPlp ?? ''),
            alasanPenunjukan: h?.tpsTujuan?.alasanPenunjukan || s?.tpsTujuan?.alasanPenunjukan || h.alasanPenunjukan || '',
            yorAsal: Number(h.yorAsal ?? s.yorAsal ?? 0),
            yorTujuan: Number(h.yorTujuan ?? h?.tpsTujuan?.yorTujuan ?? s.yorTujuan ?? 0),
            callSign: h.callSign || s.callSign || '',
            namaAngkut: h.namaAngkut || s.namaAngkut || '',
            noVoyFlight: h.noVoyFlight || s.noVoyFlight || '',
            tanggalTiba: toMoment(h.tanggalTiba ?? s.tanggalTiba),
            nomorPos: h.nomorPos || h.nomor_pos || s.nomorPos || s.nomor_pos || h.noPos || h.no_pos || s.noPos || s.no_pos || '',
            nomorBc11: h.nomorBc11 || s.nomorBc11 || '',
            tanggalBc11: toMoment(h.tanggalBc11 ?? s.tanggalBc11),
            namaPemohon: h.namaPemohon || s.namaPemohon || '',
            kemasan: kemasanSrc.length ? kemasanSrc.map((k) => ({
                jenisKemasan: k.jenisKemasan || k.jns_kemas || '',
                jumlahKemasan: Number(k.jumlahKemasan ?? k.jml_kemas ?? 0),
                nomorBlAwb: k.nomorBlAwb || k.no_bl_awb || '',
                tanggalBlAwb: toMoment(k.tanggalBlAwb ?? k.tgl_bl_awb),
            })) : [DefKemasan()],
            kontainer: kontainerSrc.length ? kontainerSrc.map((k) => ({
                jenisMuat: String(k.jenisMuat ?? ''),
                nomorKontainer: k.nomorKontainer || '',
                ukuranKontainer: String(k.ukuranKontainer ?? ''),
            })) : [DefKontainer()],
        };
    };

    // Isi otomatis form dari SAMPLE_DATA (pertahankan mode create/edit saat ini)
    // Nomor Surat selalu random 6 digit agar unik tiap klik
    const fillSample = () => {
        const mapped = mapEditToForm(SAMPLE_DATA);
        setModel((prev) => ({
            ...mapped,
            nomorSurat: String(Math.floor(Math.random() * 1e6)).padStart(6, '0'),
            _id: prev?._id ?? null,
            isCreate: prev?.isCreate ?? true,
        }));
    };

    const buildPayload = (m) => {
        const yorTujuan = Number(m.yorTujuan ?? 0);
        return {
            // sertakan _id saat mode Edit agar backend tahu record yang diupdate
            ...(m._id ? { _id: m._id } : {}),
            header: {
                callSign: m.callSign || '',
                gudangAsal: (m.gudangAsal || '').toUpperCase(),
                gudangTujuan: (m.gudangTujuan || '').toUpperCase(),
                kodeAlasanPlp: String(m.kodeAlasanPlp ?? ''),
                kodeKantor: m.kodeKantor || '',
                kodeTpsAsal: (m.kodeTpsAsal || '').toUpperCase(),
                kodeTpsTujuan: (m.kodeTpsTujuan || '').toUpperCase(),
                namaAngkut: (m.namaAngkut || '').toUpperCase(),
                namaPemohon: (m.namaPemohon || '').toUpperCase(),
                noVoyFlight: (m.noVoyFlight || '').toUpperCase(),
                nomorBc11: m.nomorBc11 || '',
                nomor_pos: m.nomorPos || '',
                nomorSurat: m.nomorSurat || '',
                tanggalBc11: fmtDate(m.tanggalBc11),
                tanggalSurat: fmtDate(m.tanggalSurat),
                tanggalTiba: fmtDate(m.tanggalTiba),
                tipeData: String(m.tipeData ?? '0'),
                tpsTujuan: {
                    alasanPenunjukan: m.alasanPenunjukan || '',
                    gudangTujuan: (m.gudangTujuan || '').toUpperCase(),
                    kodeTpsTujuan: (m.kodeTpsTujuan || '').toUpperCase(),
                    yorTujuan,
                },
                yorAsal: Number(m.yorAsal ?? 0),
                yorTujuan,
            },
            detil: [
                {
                    kemasan: (m.kemasan || []).map((k) => ({
                        jenisKemasan: (k.jenisKemasan || '').toUpperCase(),
                        jumlahKemasan: Number(k.jumlahKemasan ?? 0),
                        nomorBlAwb: k.nomorBlAwb || '',
                        tanggalBlAwb: fmtDate(k.tanggalBlAwb),
                    })),
                    kontainer: (m.kontainer || []).map((k) => ({
                        jenisMuat: String(k.jenisMuat ?? ''),
                        nomorKontainer: k.nomorKontainer || '',
                        ukuranKontainer: String(k.ukuranKontainer ?? ''),
                    })),
                },
            ],
        };
    };

    return (
        <FormPage
            url={'api/v1/plp/permohonan'}
            callbackPath={"/tps/plp/permohonan"}
            formValid={false}
            model={model}
            layout="vertical"
            setModel={setModel}
            formSize="default"
            title={"Permohonan PLP"}
            showModePrefix={false}
            saveLabel="Send"
            showTopBack
            hideBottomCancel
            emptyModel={DefModel}
            onEditFormEvent={mapEditToForm}
            onSaveEvent={buildPayload}
            onClearModel={() => ({ ...DefModel, kemasan: [DefKemasan()], kontainer: [DefKontainer()] })}
        >
            <Row justify="end" style={{ marginBottom: 16 }}>
                <Button icon={<ThunderboltOutlined />} onClick={fillSample}>
                    Isi Otomatis (Contoh)
                </Button>
            </Row>
            {/* ---------- FORM HEADER ---------- */}
            <Typography.Title level={5} style={{ marginBottom: 16 }}>Form Header</Typography.Title>
            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item label="Kode Kantor" name={'kodeKantor'} rules={[{ required: true }]}>
                        <Select
                            showSearch
                            placeholder="Pilih Kode Kantor"
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={(kdKantor || []).map((item) => ({
                                value: item.value,
                                label: `${item.value} - ${(item.name || '').trim()}`,
                            }))}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Tipe Data" name={'tipeData'} rules={[{ required: true }]}>
                        <Input autoComplete="off" placeholder="Masukkan Tipe Data" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Kode TPS Asal" name={'kodeTpsAsal'} rules={[{ required: true }]}>
                        <Select
                            showSearch
                            placeholder="Pilih Kode TPS Asal"
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={warehouseOpts}
                        />
                    </Form.Item>
                </Col>

                <Col span={8}>
                    <Form.Item label="Nomor Surat" name={'nomorSurat'} rules={[{ required: true, message: 'Nomor surat wajib diisi' }]}>
                        <Input autoComplete="off" placeholder="Masukkan Nomor Surat" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Tanggal Surat" required>
                        <DatePicker
                            style={{ width: '100%' }}
                            format={DATE_PICK_FMT}
                            placeholder="Select date"
                            value={model?.tanggalSurat ? moment(model.tanggalSurat) : null}
                            onChange={(date) => handleDateChange("tanggalSurat", date)}
                            allowClear={false}
                        />
                    </Form.Item>
                </Col>

                <Col span={8}>
                    <Form.Item label="Gudang Asal" name={'gudangAsal'} rules={[{ required: true }]}>
                        <Select
                            showSearch
                            placeholder="Pilih Gudang Asal"
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={gudangOpts}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Kode Alasan PLP" name={'kodeAlasanPlp'} rules={[{ required: true }]}>
                        <Select
                            showSearch
                            placeholder="Pilih Kode Alasan PLP"
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={ALASAN_LIST.map((a) => ({ value: a.value, label: a.label }))}
                            onChange={handleAlasanChange}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="YOR Asal" name={'yorAsal'}>
                        <InputNumber style={{ width: '100%' }} min={0} placeholder="Masukkan YOR Asal" />
                    </Form.Item>
                </Col>

                <Col span={8}>
                    <Form.Item label="Call Sign" name={'callSign'} rules={[{ required: true }]}>
                        <Input autoComplete="off" placeholder="Masukkan Call Sign" onInput={e => e.target.value = e.target.value.toUpperCase()} />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Nama Angkut" name={'namaAngkut'} rules={[{ required: true }]}>
                        <Input autoComplete="off" placeholder="Masukkan Nama Angkut" onInput={e => e.target.value = e.target.value.toUpperCase()} />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="No Voy/Flight" name={'noVoyFlight'} rules={[{ required: true }]}>
                        <Input autoComplete="off" placeholder="Masukkan No Voy/Flight" onInput={e => e.target.value = e.target.value.toUpperCase()} />
                    </Form.Item>
                </Col>

                <Col span={8}>
                    <Form.Item label="Tanggal Tiba" required>
                        <DatePicker
                            style={{ width: '100%' }}
                            format={DATE_PICK_FMT}
                            placeholder="Select date"
                            value={model?.tanggalTiba ? moment(model.tanggalTiba) : null}
                            onChange={(date) => handleDateChange("tanggalTiba", date)}
                            allowClear={false}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Nomor Pos" name={'nomorPos'}>
                        <Input autoComplete="off" placeholder="Masukkan Nomor Pos" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Nomor BC 1.1" name={'nomorBc11'} rules={[{ required: true }]}>
                        <Input autoComplete="off" placeholder="Masukkan Nomor BC 1.1" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Tanggal BC 1.1" required>
                        <DatePicker
                            style={{ width: '100%' }}
                            format={DATE_PICK_FMT}
                            placeholder="Select date"
                            value={model?.tanggalBc11 ? moment(model.tanggalBc11) : null}
                            onChange={(date) => handleDateChange("tanggalBc11", date)}
                            allowClear={false}
                        />
                    </Form.Item>
                </Col>

                <Col span={8}>
                    <Form.Item label="Nama Pemohon" name={'namaPemohon'} rules={[{ required: true }]}>
                        <Input autoComplete="off" placeholder="Masukkan Nama Pemohon" onInput={e => e.target.value = e.target.value.toUpperCase()} />
                    </Form.Item>
                </Col>
            </Row>

            {/* ---------- FORM TPS TUJUAN ---------- */}
            <Typography.Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>Form TPS Tujuan</Typography.Title>
            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item label="Kode TPS Tujuan" name={'kodeTpsTujuan'} rules={[{ required: true }]}>
                        <Select
                            showSearch
                            placeholder="Masukkan Kode TPS Tujuan"
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={warehouseOpts}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Gudang Tujuan" name={'gudangTujuan'} rules={[{ required: true }]}>
                        <Select
                            showSearch
                            placeholder="Masukkan Gudang Tujuan"
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={gudangOpts}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="YOR Tujuan" name={'yorTujuan'}>
                        <InputNumber style={{ width: '100%' }} min={0} placeholder="Masukkan YOR Tujuan" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Alasan Penunjukan" name={'alasanPenunjukan'} rules={[{ required: true }]}>
                        <Input autoComplete="off" placeholder="Masukkan Alasan Penunjukan" />
                    </Form.Item>
                </Col>
            </Row>

            {/* ---------- FORM DATA KEMASAN ---------- */}
            <Card
                title="Form Data Kemasan"
                size="small"
                style={{ marginBottom: 16 }}
                extra={<Button size="small" icon={<PlusOutlined />} onClick={addKemasan}>Tambah Kemasan</Button>}
            >
                {(model.kemasan || []).map((k, idx) => (
                    <Row gutter={12} key={idx} align="bottom" style={{ marginBottom: 8 }}>
                        <Col span={4}>
                            <Form.Item label={idx === 0 ? 'Jenis Kemasan' : ''} required>
                                <Select
                                    showSearch
                                    value={k.jenisKemasan || undefined}
                                    placeholder="PK"
                                    optionFilterProp="label"
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={kemasanOpts}
                                    onChange={(v) => updateKemasan(idx, { jenisKemasan: v })}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item label={idx === 0 ? 'Jumlah Kemasan' : ''} required>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={1}
                                    value={k.jumlahKemasan}
                                    onChange={(v) => updateKemasan(idx, { jumlahKemasan: v })}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={7}>
                            <Form.Item label={idx === 0 ? 'Nomor BL/AWB' : ''} required>
                                <Input
                                    value={k.nomorBlAwb}
                                    placeholder="406-93764506"
                                    autoComplete="off"
                                    onChange={(e) => updateKemasan(idx, { nomorBlAwb: e.target.value })}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={7}>
                            <Form.Item label={idx === 0 ? 'Tanggal BL/AWB' : ''} required>
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format={DATE_PICK_FMT}
                                    value={k.tanggalBlAwb ? moment(k.tanggalBlAwb) : null}
                                    onChange={(date) => updateKemasan(idx, { tanggalBlAwb: date })}
                                    allowClear={false}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={2}>
                            <Form.Item label={idx === 0 ? ' ' : ''}>
                                <Button danger icon={<DeleteOutlined />} onClick={() => removeKemasan(idx)} disabled={(model.kemasan || []).length <= 1} />
                            </Form.Item>
                        </Col>
                    </Row>
                ))}
            </Card>

            {/* ---------- FORM DATA KONTAINER ---------- */}
            <Card
                title="Form Data Kontainer"
                size="small"
                style={{ marginBottom: 8 }}
                extra={<Button size="small" icon={<PlusOutlined />} onClick={addKontainer}>Tambah Kontainer</Button>}
            >
                {(model.kontainer || []).map((k, idx) => (
                    <Row gutter={12} key={idx} align="bottom" style={{ marginBottom: 8 }}>
                        <Col span={8}>
                            <Form.Item label={idx === 0 ? 'Nomor Kontainer' : ''}>
                                <Input
                                    value={k.nomorKontainer}
                                    placeholder="Masukkan Nomor Kontainer"
                                    autoComplete="off"
                                    onInput={e => e.target.value = e.target.value.toUpperCase()}
                                    onChange={(e) => updateKontainer(idx, { nomorKontainer: e.target.value })}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={7}>
                            <Form.Item label={idx === 0 ? 'Ukuran Kontainer' : ''}>
                                <Select
                                    value={k.ukuranKontainer || undefined}
                                    placeholder="Pilih Ukuran Kontainer"
                                    options={[
                                        { value: '60 FEET', label: '60 FEET' },
                                        { value: '20 FEET', label: '20 FEET' },
                                        { value: '40 FEET', label: '40 FEET' },
                                        { value: '45 FEET', label: '45 FEET' },
                                    ]}
                                    onChange={(v) => updateKontainer(idx, { ukuranKontainer: v })}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={7}>
                            <Form.Item label={idx === 0 ? 'Jenis Muat' : ''}>
                                <Select
                                    value={k.jenisMuat || undefined}
                                    placeholder="Pilih Jenis Muat"
                                    options={[
                                        { value: '4', label: '4 - EMPTY' },
                                        { value: '7', label: '7 - LCL' },
                                        { value: '8', label: '8 - FCL' },
                                    ]}
                                    onChange={(v) => updateKontainer(idx, { jenisMuat: v })}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={2}>
                            <Form.Item label={idx === 0 ? ' ' : ''}>
                                <Button danger icon={<DeleteOutlined />} onClick={() => removeKontainer(idx)} disabled={(model.kontainer || []).length <= 1} />
                            </Form.Item>
                        </Col>
                    </Row>
                ))}
            </Card>
        </FormPage>
    )
}
